import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CreditCard, Plus, Search, Wallet } from 'lucide-react';
import { formatIqd, fromMillidinars, type CustomerAccountDto } from '@gc/shared';

import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { useAuth } from '@/features/auth/useAuth';
import { useDrawerCurrent } from '@/features/cash-drawer/useCashDrawer';
import { api, ApiError } from '@/lib/api';
import { CustomerFormDialog } from './CustomerFormDialog';
import { CustomerProfileDrawer } from './CustomerProfileDrawer';
import { ListPager } from './ListPager';
import { EMPTY_CUSTOMER_FILTERS, useCustomers, type CustomerFilters } from './useCustomers';

const SORTS = ['name', 'balance', 'points', 'recent'] as const;

/**
 * Where the cash link beside the filters goes (owner, 2026-10-12): the box
 * screen until the owner starts the cash drawer — the main cashier still does
 * the normal counted box reset that day — and the cash drawer from then on.
 * Anything short of a known "in use" keeps the box link: it is the screen the
 * address always named, and once the drawer is in use the server refuses every
 * box reset itself.
 */
export function cashLinkFor(drawerInUse: boolean): { to: string; labelKey: string } {
  return drawerInUse
    ? { to: '/cash-drawer', labelKey: 'cashDrawer.nav' }
    : { to: '/box-balance', labelKey: 'boxBalance.title' };
}

/**
 * Customers — SPEC §5.5.
 *
 * One search box over name, phone and card, because that is the order a cashier
 * tries things in when someone is standing at the counter: they say their name,
 * they give a number, or they put a card on the reader.
 */
export function CustomersPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  // One read and no live tick: this only picks the link.
  const { current: drawer } = useDrawerCurrent(true);
  const cashLink = cashLinkFor(drawer?.enabled === true);

  const [filters, setFilters] = useState<CustomerFilters>(EMPTY_CUSTOMER_FILTERS);
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [cardTapError, setCardTapError] = useState<string | null>(null);

  const { data, loading, error, reload } = useCustomers(filters, page);

  // Any filter change starts the list over: page 3 of a search with one page
  // of results is an empty grid for no visible reason.
  useEffect(() => {
    setPage(1);
  }, [filters.search, filters.includeInactive, filters.withBalanceOnly, filters.sort]);

  // Debounced: typing a phone number should not fire a request per digit.
  useEffect(() => {
    const timer = setTimeout(
      () => setFilters((current) => ({ ...current, search: searchDraft })),
      300,
    );
    return () => clearTimeout(timer);
  }, [searchDraft]);

  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  /**
   * Read a card and open the matching account.
   *
   * The reader types the UID into the focused field like a keyboard, so the
   * search box already holds it by the time this runs; sending it through the
   * card-tap route (rather than just searching) is what writes the
   * `CardLoginEvent` the §5.7 Cards report is built on.
   */
  async function handleCardTap(): Promise<void> {
    setCardTapError(null);
    const cardId = searchDraft.trim();
    if (!cardId) {
      setCardTapError(t('customers.card.prompt'));
      return;
    }

    try {
      const account = await api.post<CustomerAccountDto>('/customers/card-tap', { cardId });
      setOpenId(account.id);
    } catch (caught) {
      setCardTapError(
        caught instanceof ApiError ? caught.message : t('customers.card.notRecognised'),
      );
    }
  }

  const controlClass =
    'rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('customers.title')}</h1>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder={t('customers.searchPlaceholder')}
            className={`${controlClass} w-72 ps-8`}
          />
        </div>

        <FilterDisclosure
          badge={(filters.withBalanceOnly ? 1 : 0) + (filters.includeInactive ? 1 : 0)}
        >
          <button
            type="button"
            onClick={() => void handleCardTap()}
            title={t('customers.card.prompt')}
            className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
          >
            <CreditCard className="h-4 w-4" aria-hidden />
            {t('customers.card.tap')}
          </button>

          <div className="flex-1" />

          <label className="flex items-center gap-1.5 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={filters.withBalanceOnly}
              onChange={(event) =>
                setFilters((current) => ({ ...current, withBalanceOnly: event.target.checked }))
              }
              className="h-4 w-4 accent-[var(--brand-primary)]"
            />
            {t('customers.withBalanceOnly')}
          </label>

          <label className="flex items-center gap-1.5 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={filters.includeInactive}
              onChange={(event) =>
                setFilters((current) => ({ ...current, includeInactive: event.target.checked }))
              }
              className="h-4 w-4 accent-[var(--brand-primary)]"
            />
            {t('customers.showInactive')}
          </label>

          <select
            value={filters.sort}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                sort: event.target.value as CustomerFilters['sort'],
              }))
            }
            aria-label={t('customers.sortBy')}
            className={controlClass}
          >
            {SORTS.map((sort) => (
              <option key={sort} value={sort}>
                {t(`customers.sort.${sort}`)}
              </option>
            ))}
          </select>

          {/* The cashier's shortcut to where cash is handed over. The sidebar
            carries the Cash drawer entry for everyone; the box screen has no
            entry of its own, so until the owner starts the drawer this link
            (and the Cash drawer page's pointer) is how the box reset is
            reached. */}
          <Link
            to={cashLink.to}
            className="flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
          >
            <Wallet className="h-4 w-4" aria-hidden />
            {t(cashLink.labelKey)}
          </Link>
        </FilterDisclosure>

        {can('customers.manage') ? (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('customers.addCustomer')}
          </button>
        ) : null}
      </header>

      {cardTapError ? (
        <p role="alert" className="px-4 pb-2 text-sm text-danger">
          {cardTapError}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : !data || data.rows.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.noResults')}</p>
        ) : (
          <>
            {/* Phone: a card per customer. The whole row was already one tap
              into the profile; the card keeps that and drops the sideways drag. */}
            <div className="space-y-2 pb-3 md:hidden">
              {data.rows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setOpenId(row.id)}
                  className="block w-full rounded-card border border-subtle bg-raised p-3 text-start hover:bg-hover"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-content">
                      {row.fullName}
                      {row.isActive ? null : (
                        <span className="ms-2 text-xs font-normal text-content-muted">
                          {t('customers.inactive')}
                        </span>
                      )}
                    </span>
                    <span className="tabular text-sm font-semibold text-content">
                      {money(row.balanceMd)}
                    </span>
                  </div>
                  <p className="tabular mt-1 flex flex-wrap gap-x-3 text-xs text-content-muted">
                    {row.phone ? <span dir="ltr">{row.phone}</span> : null}
                    {row.cardId ? (
                      <span>
                        {t('customers.cardId')}: {row.cardId}
                      </span>
                    ) : null}
                    <span>
                      {t('customers.points')}: {row.points}
                    </span>
                    {row.discountTimePercent ||
                    row.discountCafePercent ||
                    row.discountKitchenPercent ||
                    row.discountShishaPercent ? (
                      <span dir="ltr">
                        {`${row.discountTimePercent}/${row.discountCafePercent}/${row.discountKitchenPercent}/${row.discountShishaPercent}%`}
                      </span>
                    ) : null}
                  </p>
                </button>
              ))}
              {/* The liability line the owner reads at a glance, same as tfoot. */}
              <p className="flex justify-between rounded-card border border-strong bg-raised p-3 text-sm font-semibold text-content">
                <span>
                  {t('common.itemCount')}: {data.summary.itemCount}
                </span>
                <span className="tabular">
                  {t('customers.totalBalance')}: {money(data.summary.totalBalanceMd)}
                </span>
              </p>
            </div>

            <div className="overflow-x-auto rounded-card border border-subtle max-md:hidden">
              <table className="w-full text-sm">
                <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
                  <tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('common.phoneNumber')}</Th>
                    <Th>{t('customers.cardId')}</Th>
                    <Th align="end">{t('customers.balance')}</Th>
                    <Th align="end">{t('customers.points')}</Th>
                    <Th align="end">{t('customers.discount')}</Th>
                    <Th>{t('customers.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setOpenId(row.id)}
                      className="cursor-pointer border-t border-subtle hover:bg-hover"
                    >
                      <Td strong>{row.fullName}</Td>
                      <Td>{row.phone ?? '—'}</Td>
                      <Td>{row.cardId ?? '—'}</Td>
                      {/* A negative balance is the venue's money in a customer's
                        pocket — danger red, never a quiet grey (card 09). */}
                      <Td
                        align="end"
                        strong
                        className={row.balanceMd.startsWith('-') ? 'font-medium text-danger' : ''}
                      >
                        {money(row.balanceMd)}
                      </Td>
                      <Td align="end">{row.points}</Td>
                      {/* Wave 3: the buckets as Reservations/Cafe/Kitchen/Shisha. */}
                      <Td align="end">
                        {row.discountTimePercent ||
                        row.discountCafePercent ||
                        row.discountKitchenPercent ||
                        row.discountShishaPercent ? (
                          <span dir="ltr">
                            {`${row.discountTimePercent}/${row.discountCafePercent}/${row.discountKitchenPercent}/${row.discountShishaPercent}%`}
                          </span>
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td>
                        {row.isActive ? (
                          t('customers.active')
                        ) : (
                          <span className="text-content-muted">{t('customers.inactive')}</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                {/* Footer totals the WHOLE filtered set, not this page — the
                  outstanding balance is a liability the owner reads at a glance. */}
                <tfoot className="border-t border-strong bg-raised text-sm font-semibold">
                  <tr>
                    <Td>
                      {t('common.itemCount')}: {data.summary.itemCount}
                    </Td>
                    <Td />
                    <Td align="end">{t('customers.totalBalance')}</Td>
                    <Td align="end">{money(data.summary.totalBalanceMd)}</Td>
                    <Td align="end">{data.summary.totalPoints}</Td>
                    <Td />
                    <Td />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* The footer counts every account the filter matches; the pager
              is how the ones past the first page are reached without a
              search (audit, 2026-09-23: 47 of 97 were unreachable). */}
            <ListPager
              page={page}
              hasMore={data.hasMore}
              onPage={setPage}
              previousLabel={t('customers.previousPage')}
              nextLabel={t('customers.nextPage')}
            />
          </>
        )}
      </div>

      {adding ? (
        <CustomerFormDialog
          customer={null}
          onClose={() => setAdding(false)}
          onSaved={() => void reload()}
        />
      ) : null}

      {openId ? (
        <CustomerProfileDrawer
          customerId={openId}
          currencySymbol={currencySymbol}
          onClose={() => setOpenId(null)}
          onChanged={() => void reload()}
        />
      ) : null}
    </div>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'end' }) {
  return (
    <th className={`px-3 py-2 font-medium ${align === 'end' ? 'text-end' : 'text-start'}`}>
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  strong,
  className = '',
}: {
  children?: React.ReactNode;
  align?: 'end';
  strong?: boolean;
  className?: string;
}) {
  return (
    <td
      className={[
        'px-3 py-2',
        align === 'end' ? 'tabular text-end' : 'text-start',
        // A caller's colour REPLACES the default pair — two text-* utilities
        // on one cell would resolve by stylesheet order, not by intent.
        className || (strong ? 'font-medium text-content' : 'text-content-secondary'),
      ].join(' ')}
    >
      {children}
    </td>
  );
}
